import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Check, CheckCheck, Clock, MessageCircle, Send, TriangleAlert } from 'lucide-react';
import type {
  MessageLogEntry,
  NotificationEvent,
  OrderMessagePreview,
  OrderWithItems,
} from '@tailoring-crm/shared';
import { useAuth } from '../../lib/auth/auth-context.js';
import {
  useOrderMessagePreview,
  useOrderMessages,
  useSendOrderMessage,
} from '../../lib/notifications/use-notifications.js';
import { formatDateTime } from '../../lib/format.js';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from '@tailoring-crm/ui';

/**
 * What this customer has been told, and how to tell them something now.
 *
 * The card is built around one fact about the market it serves: most shops will not have
 * connected WhatsApp on the day they first open this screen, and some never will. So the
 * primary action is not "send" — it is "send it, one way or the other". With Relay connected
 * the message goes by itself; without it, the same message is composed from the same order data
 * and one tap opens WhatsApp on the phone in the staff member's hand with it already typed.
 *
 * Nothing is ever sent without being read first. The dialog shows the exact words, and the send
 * button sits underneath them — a manual send skips the deduplication guard by design, so the
 * person pressing it is the only check there is.
 */

const STATUS_ICON: Record<string, ReactElement> = {
  delivered: <Check aria-hidden="true" className="size-3.5" />,
  read: <CheckCheck aria-hidden="true" className="size-3.5" />,
  scheduled: <Clock aria-hidden="true" className="size-3.5" />,
  queued: <Clock aria-hidden="true" className="size-3.5" />,
  failed: <TriangleAlert aria-hidden="true" className="size-3.5" />,
  dead_letter: <TriangleAlert aria-hidden="true" className="size-3.5" />,
};

function statusVariant(status: MessageLogEntry['status']): 'secondary' | 'warning' | 'destructive' {
  if (status === 'dead_letter' || status === 'failed') return 'destructive';
  if (status === 'scheduled' || status === 'queued') return 'warning';
  return 'secondary';
}

export function OrderUpdatesCard({ order }: { order: OrderWithItems }): ReactElement | null {
  const [composing, setComposing] = useState<NotificationEvent | null>(null);
  const messages = useOrderMessages(order.id);
  const preview = useOrderMessagePreview(order.id, composing);
  const send = useSendOrderMessage(order.id);

  return (
    <CustomerUpdatesCard
      customerName={order.customerName}
      data={messages.data}
      composing={composing}
      onCompose={setComposing}
      preview={preview.data}
      previewPending={preview.isPending}
      send={send}
    />
  );
}

/**
 * The card itself, for whatever the messages are about — an order here, an alteration on its
 * own page (`AlterationUpdatesCard`). The caller owns the three queries; this owns the words.
 */
export function CustomerUpdatesCard({
  customerName,
  data,
  composing,
  onCompose,
  preview,
  previewPending,
  send,
}: {
  customerName: string;
  data:
    | {
        relayConnected: boolean;
        messages: MessageLogEntry[];
        options: { event: NotificationEvent }[];
      }
    | undefined;
  composing: NotificationEvent | null;
  onCompose: (event: NotificationEvent | null) => void;
  preview: OrderMessagePreview | undefined;
  previewPending: boolean;
  send: { mutateAsync: (event: NotificationEvent) => Promise<unknown>; isPending: boolean };
}): ReactElement | null {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { role } = useAuth();

  if (!data) return null;

  /** Mirrors `notifications.send` in the matrix; the API refuses a tailor regardless. */
  const maySend = role !== 'tailor';

  const onSend = async (): Promise<void> => {
    if (!composing) return;
    try {
      await send.mutateAsync(composing);
      toast({ title: t('orderUpdates.sent'), variant: 'success' });
      onCompose(null);
    } catch (error) {
      toast({
        title: error instanceof Error ? error.message : t('error.unexpected'),
        variant: 'destructive',
      });
    }
  };

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{t('orderUpdates.heading')}</h3>
        {!data.relayConnected ? (
          /**
           * The prompt, at the only moment it is welcome: the shop is looking at the feature
           * and about to do by hand what connecting would automate.
           */
          <Link
            to="/settings/integrations"
            className="text-xs font-medium text-primary underline-offset-2 hover:underline"
          >
            {t('orderUpdates.connectPrompt')}
          </Link>
        ) : null}
      </div>

      {data.messages.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('orderUpdates.nothingSentYet')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {data.messages.map((message) => (
            <li
              key={message.id}
              className="flex flex-col gap-1 rounded-md bg-muted/40 p-2 text-sm"
              data-testid="order-message"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{t(`notifications.event.${message.event}`)}</span>
                <Badge variant={statusVariant(message.status)} className="gap-1">
                  {STATUS_ICON[message.status] ?? null}
                  {t(`notifications.status.${message.status}`)}
                </Badge>
                {message.manual ? (
                  <Badge variant="outline">{t('orderUpdates.sentByHand')}</Badge>
                ) : null}
                <span className="text-xs text-muted-foreground">
                  {formatDateTime(message.createdAt)}
                </span>
              </div>
              {message.renderedBody ? (
                <p className="whitespace-pre-line text-muted-foreground">{message.renderedBody}</p>
              ) : null}
              {message.error ? <p className="text-xs text-destructive">{message.error}</p> : null}
            </li>
          ))}
        </ul>
      )}

      {maySend ? (
        <div className="flex flex-wrap gap-2">
          {data.options.map((option) => (
            <Button
              key={option.event}
              size="sm"
              variant="outline"
              onClick={() => onCompose(option.event)}
            >
              <MessageCircle aria-hidden="true" />
              {t(`notifications.event.${option.event}`)}
            </Button>
          ))}
        </div>
      ) : null}

      <Dialog open={composing !== null} onOpenChange={(open) => !open && onCompose(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('orderUpdates.previewTitle')}</DialogTitle>
            <DialogDescription>
              {t('orderUpdates.previewDescription', { name: customerName })}
            </DialogDescription>
          </DialogHeader>

          {previewPending ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : preview ? (
            <div className="flex flex-col gap-3">
              {/*
                The template's own wording when there is one, and the shop's own words when
                there is not. Either way it is the message that will actually go.
              */}
              <p
                className="whitespace-pre-line rounded-md bg-muted p-3 text-sm"
                data-testid="message-preview"
              >
                {preview.rendered || preview.fallbackText}
              </p>

              {preview.problems.length > 0 ? (
                <ul className="flex flex-col gap-1 text-xs text-muted-foreground">
                  {preview.problems.map((problem) => (
                    <li key={problem} className="flex items-center gap-1">
                      <TriangleAlert aria-hidden="true" className="size-3.5 text-warning" />
                      {problem}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

          <DialogFooter>
            {/*
              Always offered, never hidden. Even a shop with Relay connected sometimes wants to
              send from its own number — and for a shop without it, this IS the feature.
            */}
            {preview ? (
              <Button asChild variant="outline">
                <a href={preview.fallbackUrl} target="_blank" rel="noreferrer">
                  <MessageCircle aria-hidden="true" />
                  {t('orderUpdates.openWhatsApp')}
                </a>
              </Button>
            ) : null}
            <Button
              disabled={!preview?.canSendAutomatically || send.isPending}
              onClick={() => void onSend()}
            >
              <Send aria-hidden="true" />
              {t('orderUpdates.sendNow')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default OrderUpdatesCard;
